import type { nouvelleReservation as Fr } from '../fr/nouvelleReservation';

const pl = (n: number, un: string, plusieurs: string) => `${n} ${n === 1 ? un : plusieurs}`;

/** « nouvelleReservation » in English — typed on the French: a missing sentence does not compile. */
export const nouvelleReservation: typeof Fr = {
  etapes: ['dates and vehicle', 'customer', 'price and confirmation'],
  etape: (n, nom) => `Step ${n} of 3 — ${nom}`,
  retourLe: (date, jours) => `Return on ${date} — ${pl(jours, 'day', 'days')}. The price does not change.`,
  sinonHeure: (heure) => `(otherwise ${heure} at check-out and return)`,
  article02: (ouverture, fermeture, heure) =>
    `Article 02 requires returns between ${ouverture} and ${fermeture}. The return is planned at ${heure} — the agency will be closed.`,
  totalMois: (n) => `Total ${pl(n, 'month', 'months')}`,
  totalJours: (n) => `Total ${n} d`,
  ageMinimum: (age) => `${age} years old — minimum age 28 required (eligibility rule)`,
  ans: (n) => `${n} years old`,
  permisMinimum: (n) => `Licence held ${pl(n, 'year', 'years')} — minimum 5 years required (eligibility rule)`,
  anciennete: (n) => `${pl(n, 'year', 'years')} of driving`,
  derogationAccordee: (message) => `Override granted — ${message}`,
  motif: (motif) => `Reason: ${motif}`,
  prixNegocieMois: (devise) => `Negotiated price / month (${devise})`,
  moisFoisPrix: (n, total) => `${pl(n, 'month', 'months')} × this price = ${total} — whatever the number of days.`,
  prixNegocieJour: (devise) => `Negotiated price / day (${devise})`,
  ecart: (pct) => `${pct} % compared with the reference rate`,
  remiseTotal: (devise) => `Discount on the total (${devise})`,
  joursFoisTarif: (jours, tarif, total) => `${pl(jours, 'day', 'days')} × ${tarif} = ${total}`,
  localeNombre: 'en-GB',
  dernierReleve: (km) => `Last known reading: ${km} km`,
  vidangeDepassee: (km) => `Oil change OVERDUE by ${km} km — do it before the vehicle goes out.`,
  vidangeProche: (km) => `Only ${km} km left before the oil change — this rental may use them up.`,
  vidangeReste: (km) => `${km} km left before the next oil change.`,
  verseMaintenant: (devise) => `Paid now (${devise})`,
  totalAPayer: (total) => `Total to pay: ${total}`,
  echelonnes: (prevu, reste) => `${prevu} scheduled out of ${reste} still to pay`,
  ecartEcheances: (ecart) => ` — the difference is ${ecart}`,
  tranchesTexte: (verse, reste) =>
    `${verse} collected right away. The remaining ${reste} goes to “Outstanding”, marked “instalment balance” — it is an agreement, not an oversight.`,
  montantDevise: (devise) => `Amount (${devise})`,
  prixNonSaisi: (devise) => `No price entered — it would be charged 0 ${devise}. Set it in Settings.`,
  aHeure: (heure) => ` at ${heure}`,
  moisFois: (n, tarif) => `${pl(n, 'month', 'months')} × ${tarif}`,
  soitJours: (n) => `i.e. ${pl(n, 'day', 'days')} — the price does not change`,
  joursFois: (n, tarif) => `${pl(n, 'day', 'days')} x ${tarif}`,
  versesDettes: (verse, reste) => `${verse} paid · ${reste} outstanding`,
  taxeDefaut: 'VAT',
  taxeTaux: (taxe, taux) => `${taxe} ${taux} %`,
  locationAvecTaxe: (taxe, taux) => `Rental with ${taxe} (${taux} %)`,
  prixTtc: (taxe) => `The price becomes tax-inclusive, and the invoice will carry the same ${taxe}.`,
  champDerogation: (message) => `eligibility rule override — ${message}`,
  titre: 'New rental',
  datesVehicule: 'Dates and vehicle',
  facturerJour: 'Charge by the day',
  facturerMois: 'Charge by the month',
  depart: 'Check-out',
  combienMois: 'How many months',
  retour: 'Return',
  preciserHeures: 'Specify times',
  retourApresDepart: 'The return must be after the check-out.',
  rienDeLibre: 'Nothing available for this period',
  changezDates: 'Change the dates, or open the Availability screen to see when vehicles become available.',
  thVehicule: 'Vehicle',
  thImmatriculation: 'Registration',
  tarifMois: 'Monthly rate',
  tarifJour: 'Daily rate',
  pasTarifMois: 'no monthly rate — set it in “My vehicles”',
  client: 'Customer',
  rechercher: 'Search by name or phone',
  rechercherExemple: 'Bouzidi, 0661…',
  nouveauClient: 'New customer',
  listeNoire: 'Blacklist',
  verificationImpossible: 'Check failed',
  verificationTexte: 'The eligibility rules — minimum age and years of driving — could not be checked with the server. Until the check succeeds, the rental cannot be created: without it, a non-compliant customer would get through unnoticed.',
  dateNaissance: 'Date of birth',
  permisDelivreLe: 'Licence issued on',
  listeNoireTexte: 'This customer has been blacklisted by the agency. Only a manager can override this.',
  derogeableTexte: 'You can continue: the rental will be recorded with this warning, and the audit log will keep your name.',
  manqueTexte: 'This information is missing from the customer record. You can continue, but the warning will stay on the file until it is completed.',
  completerFiche: 'Complete the customer record',
  autoriserMalgreTout: 'Allow anyway',
  prevenirGerant: 'Notify a manager',
  article01Ok: 'Eligibility rule met',
  article01OkTexte: 'Age and years of driving are compliant.',
  plusDisponible: 'This vehicle is no longer available',
  plusDisponibleTexte: 'The selected vehicle no longer appears in the availability for these dates — it was probably taken in the meantime, or the dates changed. Go back to step 1 to choose another one: nothing has been saved.',
  revenirChoix: 'Back to vehicle choice',
  prixNegocie: 'Negotiated price',
  prixNegocieTexte: 'The displayed rate is a starting point. The difference is never blocked — it is measured.',
  tarifRefMois: 'Reference rate / month',
  pasTarifMoisErreur: 'This vehicle has no monthly rate. Enter one here, or set it in “My vehicles”.',
  tarifRefJour: 'Reference rate / day',
  auTarifRef: 'at the reference rate',
  motifRemise: 'Reason for the discount',
  motifRemiseAide: 'Optional — it is printed on the rental agreement.',
  motifRemiseExemple: 'Loyal customer, goodwill gesture…',
  kmIllimite: 'Unlimited mileage ',
  kmSinon: '(otherwise 250 km/day, article 05)',
  kmCompteur: 'Odometer reading',
  aucuneVidange: 'No oil change recorded for this vehicle: impossible to say what is left.',
  commentPaie: 'How the customer pays',
  paiementSurPlace: 'Pay at the counter',
  paiementTranches: 'Pay in instalments',
  paiementDiffere: 'Pay later',
  payeEn: 'Paid by',
  especes: 'Cash',
  carte: 'Card',
  cheque: 'Cheque',
  virement: 'Bank transfer',
  traite: 'Bill of exchange',
  calendrier: 'Upcoming instalment schedule',
  montant: 'Amount',
  retirer: 'Remove',
  ajouterEcheance: '+ Add an instalment',
  datesImprimees: '. These dates will be printed on the rental agreement.',
  surPlaceTexte: 'You take the payment at the counter. As long as the balance is not zero, the file stays in “Outstanding”.',
  tranchesVide: 'The customer pays part now and the rest later. Enter what they pay today.',
  differeTexte: 'The customer leaves without paying: the rental goes into “Outstanding” and stays there until fully paid.',
  laCaution: 'Deposit',
  forme: 'Form',
  numeroPiece: 'Document number',
  numeroPieceAide: 'The cheque or bill of exchange number, for the deposit receipt.',
  prendsMaintenant: 'I am taking it now',
  cautionMaintenantTexte: 'The rental agreement is drawn up right away, the deposit is recorded and the deposit receipt is printed — the customer leaves with their paper.',
  cautionPlusTardTexte: 'The deposit is only noted. It will be pre-filled when the file is drawn up.',
  supplementsDepart: 'Check-out extra charges',
  supplementsTexte: 'One catalogue: the baby seat and the additional driver are in the same list as late return and fuel. These are added at check-out.',
  aucunSupplement: 'No check-out extra charges in the catalogue. A manager can add some in',
  chemineSupplements: ' Settings › Extra charges',
  recapitulatif: 'Summary',
  periode: 'Period',
  kilometrage: 'Mileage',
  illimite: 'unlimited',
  kmParJour: '250 km / day',
  cautionConvenue: 'Agreed deposit',
  paiement: 'Payment',
  parTranches: 'in instalments',
  differeCredit: 'pay later — on account',
  surPlace: 'at the counter',
  remise: 'Discount',
  totalHt: 'Total excl. tax',
  totalTtc: 'Total incl. tax',
  total: 'Total ',
  derogationArticle01: 'Eligibility rule override',
  derogationJournal: 'It will be recorded in the audit log under your name.',
  chevauchement: 'Overlap',
  impossibleCreer: 'Unable to create',
  poserOption: 'Place on hold',
  demanderResponsable: 'Ask the manager',
  motifObligatoire: 'Reason (required)',
  motifAide: 'It will be kept in the audit log, with your name.',
  motifExemple: 'Known customer, accompanied by an experienced driver…',
  accorderDerogation: 'Grant the override',
  autoriserLocation: 'Allow the rental',
  gerantPrevenu: 'Manager notified',
  envoi: 'Sending…',
  demandePartie: 'Your request has been sent',
  demandeA: 'It appears in ',
  demandeB: '“Approvals”',
  demandeC: ' for the managers, with the customer name and your reason. ',
  demandeD: 'The override is granted on this screen',
  demandeE: ': when one of them arrives, they sign in here with their account and lift the block.',
  pasLever: 'You cannot lift this block yourself. Write the reason and notify a manager: they will have to lift the block on this workstation by signing in with their account. Their name will stay in the audit log.',
};
